/* eslint-disable @typescript-eslint/no-explicit-any */
import { IInspectionRouteGroup } from "pages/thermography/model/inspection-route-group.model";
import { InspectionRouteState } from "../features/inspectionRouteSlice";
import { IInspectionRecordGroup } from "pages/thermography/model/inspection-record-group.model";
import { TreeNodeRoute, TreeNodeType } from "pages/thermography/components/InspectionRouteTree/types/treeNode";
import { EquipmentInspectionStatus } from "pages/thermography/InspectionRecord/utils/enumEquipmentInspectionStatus";
import { IInspectionRecordGroupEquipment } from "pages/thermography/model/inspection-record-group-equipment.model";

export function syncNodesToGroups(state: InspectionRouteState): IInspectionRouteGroup[] {
    // Create a deep copy of groups to avoid modifying state directly
    const newGroups = [...state.groups];

    // Loop through nodes
    state.nodes.forEach(node => {
        // 1. Para cada node, busque o group com o mesmo id, e altere o orderIndex e included do group
        const groupIndex = newGroups.findIndex(g => g.id === node.id);
        if (groupIndex !== -1) {
            newGroups[groupIndex] = {
                ...newGroups[groupIndex],
                orderIndex: node.orderIndex,
                included: node.included
            };
        }

        // 2. Para cada children no node (children nivel 1), busque o subgroup com o mesmo id, e altere o orderIndex e included do subgroup
        if (node.children) {
            node.children.forEach(child => {
                // Find subgroup in groups
                newGroups.forEach((group, groupIndex) => {
                    if (group.subGroups) {
                        const subgroupIndex = group.subGroups.findIndex(sg => sg.id === child.id);
                        if (subgroupIndex !== -1) {
                            newGroups[groupIndex].subGroups = [...(newGroups[groupIndex].subGroups || [])];
                            newGroups[groupIndex].subGroups![subgroupIndex] = {
                                ...newGroups[groupIndex].subGroups![subgroupIndex],
                                orderIndex: child.orderIndex,
                                included: child.included
                            };
                        }
                    }
                });

                // 3. Para cada children nivel 1 do node, busque o em equipments, o equipment com o mesmo id, e altere o orderIndex e included do equipments
                if (child.children) {
                    child.children.forEach(equipmentNode => {
                        // Find equipment in subgroups
                        newGroups.forEach((group, groupIndex) => {
                            if (group.subGroups) {
                                group.subGroups.forEach((subgroup, subgroupIndex) => {
                                    if (subgroup.equipments) {
                                        const equipmentIndex = subgroup.equipments.findIndex(eq => eq.id === equipmentNode.id);
                                        if (equipmentIndex !== -1) {
                                            newGroups[groupIndex].subGroups = [...(newGroups[groupIndex].subGroups || [])];
                                            newGroups[groupIndex].subGroups![subgroupIndex].equipments = [
                                                ...(newGroups[groupIndex].subGroups![subgroupIndex].equipments || [])
                                            ];
                                            newGroups[groupIndex].subGroups![subgroupIndex].equipments![equipmentIndex] = {
                                                ...newGroups[groupIndex].subGroups![subgroupIndex].equipments![equipmentIndex],
                                                orderIndex: equipmentNode.orderIndex,
                                                included: equipmentNode.included
                                            };
                                        }
                                    }
                                });
                            }
                        });
                    });
                }
            });
        }
    });
    return newGroups;
}

export const findEquipmentInGroups = (
    groups: IInspectionRecordGroup[],
    equipmentId: string
): IInspectionRecordGroupEquipment | null => {
    for (const group of groups) {
        const equipment = group.equipments?.find((eq) => eq.id === equipmentId);
        if (equipment) return equipment;
        if (group.subGroups) {
            const found = findEquipmentInGroups(group.subGroups, equipmentId);
            if (found) return found;
        }
    }
    return null;
};

export const updateEquipmentStatusInGroups = (
    groups: IInspectionRecordGroup[],
    equipmentId: string,
    status: EquipmentInspectionStatus | null
) => {
    groups.forEach((group) => {
        const equipment = group.equipments?.find((eq) => eq.id === equipmentId);
        if (equipment) equipment.status = status;
        if (group.subGroups) updateEquipmentStatusInGroups(group.subGroups, equipmentId, status);
    });
};

export const updateEquipmentStatusInNodes = (
    nodes: TreeNodeRoute[],
    equipmentId: string,
    status: EquipmentInspectionStatus | null
) => {
    nodes.forEach((node) => {
        if (node.type === TreeNodeType.ELEMENT && node.id === equipmentId) {
            node.status = status;
        }
        if (node.children?.length) {
            updateEquipmentStatusInNodes(node.children, equipmentId, status);
        }
    });
};

// export const updateEquipmentStatusInNodes = (
//     nodes: TreeNodeRoute[],
//     equipmentId: string,
//     status: EquipmentInspectionStatus | null
// ) => {
//     nodes.forEach((node) => {
//         if (node.id === equipmentId) node.status = status;
//         if (node.children) updateEquipmentStatusInNodes(node.children, equipmentId, status);
//     });
// };

export const findGroupInGroups = (
    groups: IInspectionRecordGroup[],
    groupId: string
): IInspectionRecordGroup | null => {
    for (const group of groups) {
        if (group.id === groupId) return group;
        if (group.subGroups) {
            const found = findGroupInGroups(group.subGroups, groupId);
            if (found) return found;
        }
    }
    return null;
};

export const updateGroupFinishInGroups = (
    groups: IInspectionRecordGroup[],
    groupId: string,
    finished: boolean | null,
    finishedAt: string | null
) => {
    groups.forEach((group) => {
        if (group.id === groupId) {
            group.finished = finished;
            group.finishedAt = finishedAt as any;
        }
        if (group.subGroups) updateGroupFinishInGroups(group.subGroups, groupId, finished, finishedAt);
    });
};

const cloneRecordGroup = (group: IInspectionRecordGroup): IInspectionRecordGroup => ({
    ...group,
    equipments: group.equipments?.map(eq => ({ ...eq })),
    subGroups: group.subGroups ? group.subGroups.map(cloneRecordGroup) : undefined,
});

export const applyNodeStatusesToRecordGroups = (
    nodes: TreeNodeRoute[],
    groups: IInspectionRecordGroup[]
): IInspectionRecordGroup[] => {
    const clonedGroups = groups.map(cloneRecordGroup);

    const traverseNodes = (list: TreeNodeRoute[]) => {
        list.forEach((node) => {
            if (node.type === TreeNodeType.ELEMENT) {
                updateEquipmentStatusInGroups(
                    clonedGroups,
                    node.id,
                    (node.status as EquipmentInspectionStatus) ?? null
                );
            }
            if (node.children) traverseNodes(node.children);
        });
    };

    traverseNodes(nodes);
    return clonedGroups;
};
